import { Component, Prop, Watch } from 'vue-property-decorator'
import { mapState } from 'vuex'
import GtrSuper from '@/modules/common/components/mixins/gtr-super.mixin'
import Container from 'typedi'
import Notification from '@/modules/common/services/notification.service'
import ErrorHandlerService from '@/modules/common/services/error-handler.service'
import { AllMedia, Media } from '@/interfaces/media.interface'

@Component({
  name: 'GtrFilePicker',
  computed: {
    ...mapState('media', ['media'])
  }
})
export default class GtrFilePicker extends GtrSuper {
  // Mime types to display and allowed for file upload component.
  @Prop({ required: false, type: String, default: '' })
  mimeTypes!: string

  @Prop({ required: false, type: String, default: '' })
  passedUrl!: string

  @Prop({ required: false, type: String, default: '' })
  classProp!: string

  @Prop({ required: false, type: Boolean, default: false })
  disabled!: false

  media!: AllMedia

  data () {
    return {
      loading: false,
      medias: [] as Media[],
      filePickerOpen: false,
      selectedMedia: null,
      mediaToDelete: null
    }
  }

  async fetchMedia () {
    try {
      this.$data.loading = true
      await this.$store.dispatch('media/getMedia', { event_uuid: this.$route.params.event_uuid })
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    } finally {
      this.$data.loading = false
    }
  }

  @Watch('media', { immediate: true })
  onMediasChange (media: AllMedia) {
    this.$data.medias = media.Active
  }

  @Watch('passedUrl')
  setSelectedMediaByUrl (payload: string) {
    this.$data.selectedMedia = this.media?.Active?.find((media: Media) => media.url === payload)
  }

  get allowedFileTypes (): string[] {
    return this.mimeTypes.split(',').reduce((accumulator: string[], mimeType) => {
      const fileType = mimeType.split('/').pop()?.split(';')[0] // return the portion of the mimeType after the slash and before any semicolon
      if (fileType) {
        accumulator.push(fileType)
      }
      return accumulator
    }, [])
  }

  get visibleMedia (): Media[] {
    if (this.allowedFileTypes.length === 0) {
      return this.$data.medias
    }

    return this.$data.medias?.filter((media: Media) => {
      const fileType = media.name?.split('.').pop() ?? ''
      return this.allowedFileTypes.includes(fileType)
    }) ?? []
  }

  get selectedMediaName (): string {
    return this.$data.selectedMedia?.name ?? ''
  }

  get selectedMediaUrl (): string {
    return this.$data.selectedMedia?.url ?? ''
  }

  async onFileUploadChange (files: File[]) {
    this.$data.loading = true
    let i = 0
    let errorCount = 0
    let lastUploadedFileUrl = null

    while (i < files.length) {
      try {
        const result = await this.$store.dispatch('media/uploadMedia', { event_uuid: this.$route.params.event_uuid, fileData: files[i] })
        lastUploadedFileUrl = result?.responseEventMedia?.data?.url
      } catch (error) {
        errorCount++
      }
      i++
    }

    await this.fetchMedia()
    this.$data.loading = false
    if (files.length === 1) {
      if (errorCount === 0) {
        Container.get(Notification).success('File successfully uploaded.')
      } else {
        Container.get(ErrorHandlerService).error('Error on file upload.')
      }
    } else if (files.length > 1) {
      if (errorCount === 0) {
        Container.get(Notification).success('Files successfully uploaded.')
      } else {
        Container.get(ErrorHandlerService).error('Error on files upload.')
      }
    }

    if (lastUploadedFileUrl) {
      this.setSelectedMediaByUrl(lastUploadedFileUrl)
    }
  }

  private isFileAnImage (filename: string) {
    const imageExtensions: string[] = ['png', 'jpg', 'jpeg', 'bmp', 'gif', 'webp']
    const extension: string | undefined = filename.split('.').pop()
    if (extension) {
      return imageExtensions.includes(extension)
    }
    return false
  }

  handleSelectMedia (media: Media) {
    this.$data.selectedMedia = media
  }

  private async handleOpenFilePicker () {
    await this.fetchMedia()
    this.$data.filePickerOpen = true
  }

  private handleCancel () {
    this.$data.selectedMedia = null
    this.$data.filePickerOpen = false
  }

  private handleClear () {
    this.$data.selectedMedia = null
    this.$emit('change', this.selectedMediaUrl)
  }

  private handleOk () {
    this.$data.filePickerOpen = false
    this.$emit('change', this.selectedMediaUrl)
  }
}
